import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppStore } from '@/store/AppStore';
import Badge, { statusTone } from '@/components/base/Badge';
import QuoteBuilder from './components/QuoteBuilder';
import QuotePreview from './components/QuotePreview';
import EmptyState from '@/components/base/EmptyState';
import { formatTL } from '@/lib/format';
import type { Quote } from '@/types';

interface PrefillState {
  prefillCustomer?: string;
  prefillService?: string;
  prefillLocation?: string;
}

export default function Teklifler() {
  const { quotes } = useAppStore();
  const location = useLocation();
  const [selected, setSelected] = useState<Quote | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [prefill, setPrefill] = useState<PrefillState>({});
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const state = location.state as PrefillState | null;
    if (state?.prefillCustomer) {
      setPrefill(state);
      setBuilderOpen(true);
    }
  }, [location.state]);

  const filtered = useMemo(() => {
    if (!statusFilter) return quotes;
    return quotes.filter((q) => q.status === statusFilter);
  }, [quotes, statusFilter]);

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
            Operasyon
          </div>
          <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Teklifler</h1>
          <p className="mt-1 text-sm text-foreground-500">Teklif oluşturun, gönderin ve onay takibini yapın.</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setPrefill({});
            setBuilderOpen(true);
          }}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-600"
        >
          <i className="ri-add-line" />
          Teklif Oluştur
        </button>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setStatusFilter('')}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
            statusFilter === '' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
          }`}
        >
          Tümü ({quotes.length})
        </button>
        {['Cevap Bekleniyor', 'Onaylandı', 'Taslak', 'Müşteriye Gönderildi', 'Revize İstendi'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
              statusFilter === s ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-background-200 bg-white">
          <EmptyState
            icon="ri-file-text-line"
            title="Teklif bulunamadı."
            description="Yeni bir teklif oluşturarak başlayın."
            actionLabel="Teklif Oluştur"
            onAction={() => setBuilderOpen(true)}
          />
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-lg border border-background-200 bg-white md:block">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-background-100 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                    <th className="px-4 py-3">Teklif No</th>
                    <th className="px-4 py-3">Müşteri</th>
                    <th className="px-4 py-3">Hizmet</th>
                    <th className="px-4 py-3">Konum</th>
                    <th className="px-4 py-3">Tarih</th>
                    <th className="px-4 py-3">Tutar</th>
                    <th className="px-4 py-3">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => (
                    <tr
                      key={q.id}
                      onClick={() => setSelected(q)}
                      className="cursor-pointer border-b border-background-100 transition-colors hover:bg-background-50"
                    >
                      <td className="px-4 py-3 font-semibold text-foreground-900">{q.id}</td>
                      <td className="px-4 py-3 text-foreground-700">{q.customerName}</td>
                      <td className="px-4 py-3 text-foreground-600">{q.service}</td>
                      <td className="px-4 py-3 text-foreground-500">{q.location}</td>
                      <td className="px-4 py-3 text-foreground-500">{q.date}</td>
                      <td className="px-4 py-3 font-heading font-bold text-foreground-900">{formatTL(q.amount)}</td>
                      <td className="px-4 py-3">
                        <Badge tone={statusTone(q.status)}>{q.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {filtered.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => setSelected(q)}
                className="w-full rounded-lg border border-background-200 bg-white p-4 text-left"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-heading text-base font-bold text-foreground-950">{q.customerName}</div>
                    <div className="text-sm text-foreground-500">{q.service}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-base font-bold text-foreground-950">{formatTL(q.amount)}</div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge tone={statusTone(q.status)}>{q.status}</Badge>
                  <span className="text-xs text-foreground-400">{q.id}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {selected && <QuotePreview quote={selected} onClose={() => setSelected(null)} />}
      {builderOpen && (
        <QuoteBuilder
          open={builderOpen}
          onClose={() => setBuilderOpen(false)}
          prefillCustomer={prefill.prefillCustomer}
          prefillService={prefill.prefillService}
          prefillLocation={prefill.prefillLocation}
        />
      )}
    </div>
  );
}